import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUsersFromCache } from '../utils/userCache';
import { useSession } from '../contexts/SessionContext';
import { useCart } from '../contexts/CartContext';
// import settings from '../config/settings';
import { customers } from '../services/kioskApi';
import { 
  Box, 
  Button, 
  Container, 
  TextField, 
  Typography, 
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Paper,
  CircularProgress,
  Badge,
  IconButton,
  Grid
} from '@mui/material';
import PageLayout from './common/PageLayout';
import Alert from '@mui/material/Alert';
import { useKeyboard } from '../context/KeyboardContext';
import { 
  Search as SearchIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  ShoppingCart as ShoppingCartIcon,
  Backspace as BackspaceIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { getLineChargeTotal } from '../utils/cartPricing';

const DevoteeSearch = () => {
  const location = useLocation();
  const { session, resetTimer, setUserSelected } = useSession();
  const { cart: ctxCart = [], setCart, hydrated } = useCart();
  const navCart = (location.state && location.state.cart) || [];
  const cart = (ctxCart && ctxCart.length ? ctxCart : navCart);
  const total = cart.reduce((sum, item) => sum + getLineChargeTotal(item), 0);
  const [selectedDevotee, setSelectedDevotee] = useState(null);
  const navigate = useNavigate();
  const kb = useKeyboard();
  
  // Determine origin for navigation context
  const origin = (location?.state && location.state.from) || session?.lastOrigin || 'products';
  const originPath = origin === 'kotilinga' ? '/kotilinga' : '/shop';
  
  // Debug + hydration: if context cart empty, try hydrate from localStorage
  useEffect(() => {
    try {
      console.log('[Checkout] Context cart length:', Array.isArray(ctxCart) ? ctxCart.length : 'n/a');
      const raw = window.localStorage.getItem('kiosk_cart');
      if ((ctxCart?.length || 0) === 0 && raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          console.log('[Checkout] Hydrating cart from localStorage with', parsed.length, 'items');
          setCart(parsed);
        }
      }
    } catch (e) {
      console.warn('[Checkout] Failed to hydrate cart from localStorage', e);
    }
  // we only want to run this if ctxCart changes from empty
  }, [ctxCart, setCart]);

  // Note: do not early-return before declaring all hooks to preserve hook order across renders.
  const showHydrationLoading = (!hydrated && (!ctxCart || ctxCart.length === 0));

  // Reset timer on component mount and user interaction
  useEffect(() => {
    resetTimer();
  }, [resetTimer]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [devotees, setDevotees] = useState([]);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Define performSearch before any hooks that reference it
  const performSearch = async (searchTermTrimmed) => {
    console.log('Searching for:', searchTermTrimmed);

    if (!searchTermTrimmed) {
      setError('Please enter a search term');
      setDevotees([]);
      setSearchPerformed(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSearchPerformed(true);

    try {
      // First try to search via API
      console.log('Searching users via API...');
      const apiResults = await customers.searchUsers(searchTermTrimmed);
      
      // Live search is authoritative. Show its results (even zero) and stop here.
      const limited = (apiResults || []).slice(0, 10);
      setDevotees(limited);
      if (limited.length === 0) {
        setError('No matching devotees found. Please try a different search term.');
      }
      setIsLoading(false);
      return;
    } catch (err) {
      // Live search failed (e.g. network blip). Fall back to the cached donor snapshot.
      console.warn('Live user search failed; falling back to cached donors:', err?.message);
      const cache = getUsersFromCache();

      if (!cache || !cache.users || !Array.isArray(cache.users) || cache.users.length === 0) {
        setDevotees([]);
        setError('Unable to search right now. Please check the connection and try again.');
        setIsLoading(false);
        return;
      }

      console.log(`Searching through ${cache.users.length} cached donors (offline fallback)`);

      // Search through cached users with more flexible matching
      const results = cache.users.filter(user => {
        try {
          // Normalize user data
          const userData = {
            email: (user.email || '').toLowerCase().trim(),
            firstName: (user.first_name || '').toLowerCase().trim(),
            lastName: (user.last_name || '').toLowerCase().trim(),
            phone: (user.billing?.phone || '').replace(/[^\d+]/g, ''), // Keep only digits and +
            maskedPhone: maskPhone(user.billing?.phone || '').toLowerCase(),
            billingFirstName: (user.billing?.first_name || '').toLowerCase().trim(),
            billingLastName: (user.billing?.last_name || '').toLowerCase().trim(),
            billingEmail: (user.billing?.email || '').toLowerCase().trim(),
            billingPhone: (user.billing?.phone || '').replace(/[^\d+]/g, ''), // Keep only digits and +
            billingMaskedPhone: maskPhone(user.billing?.phone || '').toLowerCase()
          };
          
          // Create searchable strings
          const fullName = `${userData.firstName} ${userData.lastName}`.trim();
          const billingFullName = `${userData.billingFirstName} ${userData.billingLastName}`.trim();
          
          // Check for direct matches in specific fields
          const isEmailMatch = userData.email.includes(searchTermTrimmed) || 
                             userData.billingEmail.includes(searchTermTrimmed);
          
          // Check phone match (both raw and masked)
          const searchPhone = searchTermTrimmed.replace(/[^\d+]/g, '');
          const isPhoneMatch = searchPhone.length >= 4 && (
            userData.phone.includes(searchPhone) || 
            userData.billingPhone.includes(searchPhone) ||
            userData.maskedPhone.includes(searchTermTrimmed) ||
            userData.billingMaskedPhone.includes(searchTermTrimmed)
          );
          
          // Check name matches (first, last, or full name)
          const isNameMatch = [
            userData.firstName,
            userData.lastName,
            fullName,
            userData.billingFirstName,
            userData.billingLastName,
            billingFullName
          ].some(name => name.includes(searchTermTrimmed));
          
          // Check if search term matches any part of the user's data
          const allSearchableText = [
            userData.email,
            userData.firstName,
            userData.lastName,
            fullName,
            userData.phone,
            userData.maskedPhone,
            userData.billingEmail,
            userData.billingFirstName,
            userData.billingLastName,
            billingFullName,
            userData.billingPhone,
            userData.billingMaskedPhone
          ].join(' ').toLowerCase();
          
          // For multi-word searches, check if all words appear somewhere in the user's data
          const searchWords = searchTermTrimmed.split(/\s+/).filter(Boolean);
          const isMultiWordMatch = searchWords.length > 1 && 
            searchWords.every(word => allSearchableText.includes(word));
          
          return isEmailMatch || isPhoneMatch || isNameMatch || isMultiWordMatch;
          
        } catch (error) {
          console.error('Error processing user:', error);
          return false;
        }
      });
      
      console.log('Search results:', results);
      
      // Limit to 10 results
      const limitedResults = results.slice(0, 10);
      setDevotees(limitedResults);

      if (limitedResults.length === 0) {
        setError('No matching devotees found. Please try a different search term.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Custom external scrollbar refs and logic (for results list)
  const resultsRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const draggingRef = useRef(false);
  const dragOffsetRef = useRef(0);

  const updateThumb = useCallback(() => {
    const el = resultsRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb) return;
    const view = el.clientHeight;
    const scroll = el.scrollHeight;
    const trackH = track.clientHeight;
    const minThumb = 40; // minimum thumb size for touch
    const thumbH = Math.max(minThumb, Math.floor((view / scroll) * trackH));
    const maxTop = trackH - thumbH;
    const ratio = el.scrollTop / (scroll - view || 1);
    const top = Math.min(maxTop, Math.floor(ratio * maxTop));
    thumb.style.height = `${thumbH}px`;
    thumb.style.top = `${top}px`;
  }, []);

  const beginDrag = useCallback((e) => {
    const thumb = thumbRef.current;
    if (!thumb) return;
    draggingRef.current = true;
    dragOffsetRef.current = e.clientY - thumb.getBoundingClientRect().top;
    const onMove = (ev) => {
      if (!draggingRef.current) return;
      const track = trackRef.current;
      const el = resultsRef.current;
      if (!track || !el) return;
      const trackRect = track.getBoundingClientRect();
      const thumbH = thumb.clientHeight;
      const y = Math.max(0, Math.min(trackRect.height - thumbH, ev.clientY - trackRect.top - dragOffsetRef.current));
      const ratio = y / (trackRect.height - thumbH || 1);
      const maxScroll = el.scrollHeight - el.clientHeight;
      el.scrollTop = ratio * maxScroll;
    };
    const onUp = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', touchMove, { passive: false });
      window.removeEventListener('touchend', onUp);
    };
    const touchMove = (tev) => {
      tev.preventDefault();
      const t = tev.touches && tev.touches[0];
      if (!t) return;
      onMove({ clientY: t.clientY });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', touchMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, []);

  // Sync thumb on scroll/content/resize
  useEffect(() => {
    const el = resultsRef.current;
    if (!el) return;
    const onScroll = () => updateThumb();
    el.addEventListener('scroll', onScroll, { passive: true });
    updateThumb();
    const ro = new ResizeObserver(() => updateThumb());
    try { ro.observe(el); } catch {}
    const onWin = () => updateThumb();
    window.addEventListener('resize', onWin);
    return () => {
      el.removeEventListener('scroll', onScroll);
      try { ro.disconnect(); } catch {}
      window.removeEventListener('resize', onWin);
    };
  }, [devotees, updateThumb]);

  // Virtual keyboard opener for search
  const openSearchKeyboard = useCallback(() => {
    if (!kb) return;
    // If virtual keyboard is disabled, allow physical keyboard input to work normally
    if (kb.keyboardDisabled) {
      console.log('[DevoteeSearch] Virtual keyboard disabled, physical keyboard will work normally');
      resetTimer(); // Still reset timer for session management
      return;
    }
    resetTimer();
    kb.open('email', {
      showEnter: true,
      // Place the keyboard at the center of the lower half of the screen
      position: 'lowerCenter',
      hCenter: true,
      widthPct: 0.85,
      heightVh: 35,
      autoHeight: true
    }, {
      onInput: (ch) => setSearchTerm(prev => (prev || '') + ch),
      onBackspace: () => setSearchTerm(prev => (prev || '').slice(0, -1)),
      onEnter: () => {
        // Close keyboard only; debounced auto-search will handle results
        kb.close();
      },
      onClear: () => {
        setSearchTerm('');
        setDevotees([]);
        setSearchPerformed(false);
        setError(null);
      }
    });
  }, [kb, resetTimer]);
  
  // Check for cart in session - DO NOT auto-redirect; just log for diagnostics
  useEffect(() => {
    // Log mount and incoming state for debugging
    console.log('[Checkout] mounted with state:', location.state);
    
    // Defer the check slightly to allow providers/state to hydrate
    const t = setTimeout(() => {
      const incomingCart = (location.state?.cart || []);
      const sessionCart = (session?.cart || []);
      const effectiveCart = (incomingCart.length ? incomingCart : sessionCart);
      const hasCart = effectiveCart.length > 0;

      // Don't redirect if we came from payment page or returning from NewDevoteeForm
      const fromPayment = location.state?.fromPayment === true;
      const fromNewDevotee = location.state?.fromNewDevotee === true;

      console.log('[Checkout] redirect check:', { hasCart, fromPayment, fromNewDevotee, effectiveCartLen: effectiveCart.length });

      // Intentionally not redirecting; we'll allow the user to stay on /checkout
      // and present UI to proceed or go back as needed.
    }, 60);

    return () => clearTimeout(t);
  }, [location.state, session]);

  const handleBack = useCallback(() => {
    navigate('/checkout', { state: { cart, total, from: origin } });
  }, [navigate, cart, total, origin]);

  const handleNewDevotee = () => {
    resetTimer();
    navigate('/checkout/new-devotee', { 
      state: { cart, total, from: origin },
      replace: true
    });
  };
  
  const handleDevoteeSelect = (devotee) => {
    resetTimer();
    setSelectedDevotee(devotee);
    
    // Set userSelectedFlag to true in session with the selected devotee
    setUserSelected(true, devotee);
    
    // Log the selection
    console.log('User selected, navigating to payment:', devotee);
    
    // Navigate to payment page with the selected devotee and cart data
    navigate('/payment', { 
      state: { 
        cart,
        devotee: devotee,
        total,
        fromCart: true,
        from: origin
      },
      replace: true
    });
  };

  

  const handleSearch = async (e) => {
    e.preventDefault();
    const term = searchTerm.trim().toLowerCase();
    if (term.length < 5) {
      setError('Please enter at least 5 characters');
      return;
    }
    await performSearch(term);
  };

  // Handle adding a new devotee
  const handleAddNewDevotee = () => {
    // Navigate to the new devotee form with the current cart
    navigate('/checkout/new-devotee', { state: { cart, from: origin } });
  };

  // Handle guest checkout
  const handleGuestCheckout = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get guest checkout email from environment variables
      const guestEmailRaw = process.env.REACT_APP_GUEST_CHECKOUT_EMAIL;
      const guestEmail = (guestEmailRaw || '').trim().toLowerCase();
      if (!guestEmail) {
        throw new Error('Guest checkout email is not configured');
      }

      // Search for guest user via API
      let apiResults = await customers.searchUsers(guestEmail);
      // Quick retry once if no results initially (e.g., backend warming)
      if (!apiResults || apiResults.length === 0) {
        await new Promise(r => setTimeout(r, 800));
        apiResults = await customers.searchUsers(guestEmail);
      }

      const guestUser = (apiResults || []).find(user => {
        const ue = (user?.email || '').trim().toLowerCase();
        return ue && ue === guestEmail;
      });

      if (!guestUser) {
        throw new Error('Guest user account not found. Please contact support.');
      }

      // Set the guest user in the session and mark as selected
      setUserSelected(true, guestUser);
      setSelectedDevotee(guestUser);
      console.log('Guest user selected:', guestUser);
      
      // Get the cart from location state or session
      const currentCart = location.state?.cart || cart || [];
      const currentTotal = currentCart.reduce((sum, item) => sum + getLineChargeTotal(item), 0);
      
      // Navigate to payment page with guest user and cart data
      navigate('/payment', { 
        state: { 
          cart: currentCart,
          devotee: guestUser,
          total: currentTotal,
          isGuestCheckout: true,
          from: origin
        },
        replace: true
      });
      
    } catch (err) {
      console.error('Guest checkout failed:', err);
      setError(err.message || 'Failed to process guest checkout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [cart, location.state, navigate, setUserSelected]);

  // Function to mask sensitive information
  const maskEmail = (email) => {
    if (!email) return '';
    const [username, domain] = email.split('@');
    if (!domain) return email; // In case there's no @ in the string
    const visibleChars = Math.min(2, username.length);
    const maskedUsername = username.substring(0, visibleChars) + '*'.repeat(Math.max(0, username.length - visibleChars));
    return `${maskedUsername}@${domain}`;
  };

  // Function to mask phone number (show only last 4 digits)
  const maskPhone = (phone) => {
    if (!phone) return '';
    const visibleDigits = 4;
    if (phone.length <= visibleDigits) return phone;
    return '*'.repeat(phone.length - visibleDigits) + phone.slice(-visibleDigits);
  };

  // Format WooCommerce customer information
  const formatCustomerInfo = (customer) => {
    const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ') || 'No Name';
    const email = customer.email ? maskEmail(customer.email) : '';
    const phone = customer.billing?.phone ? maskPhone(customer.billing.phone) : '';
    
    return { name, email, phone };
  };

  // Format WooCommerce customer name (kept for backward compatibility)
  const formatCustomerName = (customer) => {
    return formatCustomerInfo(customer).name;
  };

  // Debounced auto-search when >= 5 chars; clear when shorter
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length === 0) {
      setDevotees([]);
      setSearchPerformed(false);
      setError(null);
      return;
    }
    if (term.length < 5) {
      setSearchPerformed(false);
      setError('Type at least 5 characters to search');
      return;
    }
    setError(null);
    const id = setTimeout(() => {
      performSearch(term);
    }, 400);
    return () => clearTimeout(id);
  }, [searchTerm]);

  return (
    <PageLayout contentMaxWidth="xl">
      <Box sx={{ py: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, position: 'relative', zIndex: (theme) => theme.zIndex.appBar }}>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => { try { kb?.close && kb.close(); } catch {} try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {} navigate('/cart', { state: { from: origin } }); }}
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: '#e1bee7',
                  borderColor: '#b39ddb'
                }
              }}
            >
              Back
            </Button>
          </Box>
          <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => { try { kb?.close && kb.close(); } catch {} try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {} navigate('/home'); }}
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: '#e1bee7',
                  borderColor: '#b39ddb'
                }
              }}
            >
              Home
            </Button>
          </Box>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button
              variant="outlined"
              onClick={() => { try { kb?.close && kb.close(); } catch {} try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {} navigate('/cart', { state: { from: origin } }); }}
              startIcon={
                <Badge badgeContent={cart?.length || 0} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              }
              size="large"
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: '#e1bee7',
                  borderColor: '#b39ddb'
                }
              }}
            >
              Cart
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)' }}>
          <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: '#f3e5f5', border: '1px solid #e1bee7', borderRadius: 2, '& .MuiOutlinedInput-root': { bgcolor: '#ffffff' } }}>
            <Typography variant="h6" gutterBottom>Find Your Account</Typography>

            <form onSubmit={handleSearch}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by name or email (min 5 chars)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  error={!!error}
                  helperText={' '}
                  onFocus={openSearchKeyboard}
                  onClick={openSearchKeyboard}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: '2rem' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="backspace"
                          edge="end"
                          size="small"
                          onClick={() => setSearchTerm((prev) => (prev || '').slice(0, -1))}
                          disabled={!searchTerm}
                          sx={{ mr: 0.5 }}
                        >
                          <BackspaceIcon sx={{ fontSize: '2rem' }} />
                        </IconButton>
                        <IconButton
                          aria-label="clear search"
                          edge="end"
                          size="small"
                          onClick={() => {
                            setSearchTerm('');
                            setDevotees([]);
                            setSearchPerformed(false);
                            setError(null);
                          }}
                          disabled={!searchTerm}
                        >
                          <ClearIcon sx={{ fontSize: '2rem' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    readOnly: !kb?.keyboardDisabled, // Only readonly when virtual keyboard is enabled
                    sx: {
                      '& .MuiInputBase-input': {
                        fontSize: '2rem',
                        lineHeight: 1.4,
                        paddingTop: 1.25,
                        paddingBottom: 1.25
                      }
                    }
                  }}
                />
              </Box>
            </form>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ my: 2 }}>
                {error}
              </Alert>
            ) : searchPerformed && devotees.length === 0 ? (
              <Box sx={{ my: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography>No devotees found. Try a different search term or add a new devotee.</Typography>
              </Box>
            ) : devotees.length > 0 ? (
              <Box sx={{ position: 'relative' }}>
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    maxHeight: 300,
                    overflowY: 'auto',
                    mb: 2,
                    border: '1px solid #e1bee7',
                    borderRadius: 2,
                    bgcolor: '#ffffff',
                    pr: 4, // space for custom track
                    // Hide native scrollbars
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { width: 0, height: 0 },
                    // Force white rows and subtle hover for all list items inside
                    '& .MuiListItem-root': {
                      backgroundColor: '#ffffff !important'
                    },
                    '& .MuiListItem-root:hover': {
                      backgroundColor: '#f9f9f9 !important'
                    }
                  }}
                  ref={(el) => { resultsRef.current = el; }}
                >
                  <List className="thick-scroll">
                    {devotees.map((devotee) => {
                      const { name, email, phone } = formatCustomerInfo(devotee);
                      const isSelected = selectedDevotee?.id === devotee.id;
                      
                    return (
                      <Box component="li" sx={{ listStyle: 'none' }} key={devotee.id}>
                        <ListItem 
                          component="div"
                          sx={{
                            py: 2.5,
                            bgcolor: isSelected ? 'action.selected' : '#ffffff',
                            '&:hover': { bgcolor: isSelected ? 'action.selected' : '#f9f9f9' },
                            minHeight: 88,
                          }}
                          secondaryAction={
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={(e) => {
                                // Close virtual keyboard and blur any focused element so the same tap also selects
                                try { kb?.close && kb.close(); } catch {}
                                try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {}
                                // Proceed with selection and navigation
                                setSelectedDevotee(devotee);
                                setUserSelected(true, devotee);
                                navigate('/payment', { state: { cart, devotee: devotee, total, fromCart: true }, replace: true });
                              }}
                              size="large"
                              endIcon={<ArrowForwardIcon />}
                              sx={{ minWidth: '180px', height: 56, lineHeight: 1.2, fontSize: '1rem', touchAction: 'manipulation' }}
                              disableRipple
                              disableElevation
                            >
                              Select & Continue
                            </Button>
                          }
                        >
                          <ListItemButton
                            onClick={() => {
                              try { kb?.close && kb.close(); } catch {}
                              try { if (document?.activeElement?.blur) document.activeElement.blur(); } catch {}
                              setSelectedDevotee(devotee);
                              setUserSelected(true, devotee);
                              navigate('/payment', { state: { cart, devotee: devotee, total, fromCart: true }, replace: true });
                            }}
                            sx={{ touchAction: 'manipulation' }}
                          >
                          <ListItemText
                            primary={
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                {isSelected && <CheckCircleIcon color="primary" sx={{ flexShrink: 0, fontSize: 28 }} />}
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '1.5rem', fontWeight: 600 }}>{name}</Box>
                              </Box>
                            }
                            secondary={
                              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', ml: 4 }}>
                                {email && (
                                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EmailIcon color="action" sx={{ flexShrink: 0, fontSize: 22 }} />
                                    <Box component="span" sx={{ color: 'text.secondary', fontSize: '1.2rem' }}>{maskEmail(email)}</Box>
                                  </Box>
                                )}
                                {phone && (
                                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                                    <PhoneIcon color="action" sx={{ flexShrink: 0, fontSize: 22 }} />
                                    <Box component="span" sx={{ color: 'text.secondary', fontSize: '1.2rem' }}>{maskPhone(phone)}</Box>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                          </ListItemButton>
                        </ListItem>
                        <Divider />
                      </Box>
                    );
                  })}
                </List>
              </Paper>
              {/* Custom external scrollbar */}
              <Box
                ref={(el) => { trackRef.current = el; }}
                sx={{
                  position: 'absolute',
                  top: 10,
                  bottom: 10,
                  right: 6,
                  width: 30,
                  borderRadius: 2,
                  bgcolor: '#eeeeee',
                }}
              >
                <Box
                  ref={(el) => { thumbRef.current = el; }}
                  onMouseDown={(e) => beginDrag(e)}
                  onTouchStart={(e) => beginDrag(e.touches[0])}
                  sx={{
                    position: 'absolute',
                    left: 2,
                    width: 26,
                    borderRadius: 2,
                    bgcolor: '#a8a8a8',
                    cursor: 'grab',
                    boxShadow: 1,
                    touchAction: 'none'
                  }}
                />
              </Box>
              </Box>
            ) : null}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              If you don't have an account, continue as guest:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleGuestCheckout}
                  size="large"
                  sx={{ py: 2, height: 64 }}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  Continue as Guest
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default DevoteeSearch;
